import { WandSparkles, Sofa, Hammer, HelpCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';

// Accept both old and new naming conventions
export type PhotoCategory = 'enhanced' | 'staged' | 'renovated' | 'transformed' | 'enhance' | 'staging' | 'renovation' | 'transform';

interface PhotoBadgeProps {
  category: PhotoCategory;
  className?: string;
  variantCount?: number;
  generationError?: boolean;
}

const categoryConfig: Record<string, {
  icon: typeof WandSparkles;
  text: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
}> = {
  // New naming convention
  enhanced: {
    icon: WandSparkles,
    text: 'Poprawa jakości',
    bgColor: 'bg-blue-50/80',
    borderColor: 'border-blue-200/40',
    textColor: 'text-blue-700',
    iconColor: 'text-blue-600',
  },
  staged: {
    icon: Sofa,
    text: 'Wirtualne meblowanie',
    bgColor: 'bg-purple-50/80',
    borderColor: 'border-purple-200/40',
    textColor: 'text-purple-700',
    iconColor: 'text-purple-600',
  },
  renovated: {
    icon: Hammer,
    text: 'Renowacja',
    bgColor: 'bg-orange-50/80',
    borderColor: 'border-orange-200/40',
    textColor: 'text-orange-700',
    iconColor: 'text-orange-600',
  },
  transformed: {
    icon: Sparkles,
    text: 'Transformacja',
    bgColor: 'bg-emerald-50/80',
    borderColor: 'border-emerald-200/40',
    textColor: 'text-emerald-700',
    iconColor: 'text-emerald-600',
  },
  // Old naming convention (aliases)
  transform: {
    icon: Sparkles,
    text: 'Transformacja',
    bgColor: 'bg-emerald-50/80',
    borderColor: 'border-emerald-200/40',
    textColor: 'text-emerald-700',
    iconColor: 'text-emerald-600',
  },
  enhance: {
    icon: WandSparkles,
    text: 'Poprawa jakości',
    bgColor: 'bg-blue-50/80',
    borderColor: 'border-blue-200/40',
    textColor: 'text-blue-700',
    iconColor: 'text-blue-600',
  },
  staging: {
    icon: Sofa,
    text: 'Wirtualne meblowanie',
    bgColor: 'bg-purple-50/80',
    borderColor: 'border-purple-200/40',
    textColor: 'text-purple-700',
    iconColor: 'text-purple-600',
  },
  renovation: {
    icon: Hammer,
    text: 'Renowacja',
    bgColor: 'bg-orange-50/80',
    borderColor: 'border-orange-200/40',
    textColor: 'text-orange-700',
    iconColor: 'text-orange-600',
  },
};

// Default config for unknown categories
const defaultConfig = {
  icon: HelpCircle,
  text: 'Nieznana kategoria',
  bgColor: 'bg-gray-50/80',
  borderColor: 'border-gray-200/40',
  textColor: 'text-gray-700',
  iconColor: 'text-gray-600',
};

export function PhotoBadge({ category, className = '', variantCount, generationError }: PhotoBadgeProps) {
  const config = categoryConfig[category] || defaultConfig;
  const Icon = config.icon;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Badge
        variant="secondary"
        className={`backdrop-blur-sm ${config.bgColor} ${config.textColor} border ${config.borderColor} shadow-sm text-[11px] px-2 py-1 font-normal flex items-center gap-1.5`}
      >
        <Icon className={`w-3 h-3 ${config.iconColor}`} strokeWidth={2} />
        <span>
          {config.text}
          {variantCount != null && variantCount > 1 && ` (${variantCount})`}
        </span>
      </Badge>
      {generationError && (
        <Badge
          variant="secondary"
          className="backdrop-blur-sm bg-red-50/80 text-red-700 border border-red-200/40 shadow-sm text-[11px] px-2 py-1 font-normal flex items-center gap-1.5"
        >
          <AlertTriangle className="w-3 h-3 text-red-600" strokeWidth={2} />
          <span>Błąd renderu</span>
        </Badge>
      )}
    </div>
  );
}